"use client";

import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { Button } from "@/app/components/ui/button";
import { Flex, Text } from "@radix-ui/themes";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/components/ui/form";
import {
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import { Alert, AlertDescription, AlertTitle } from "@/app/components/ui/alert";
import axios from "axios";
import { auctionCreationSchema } from "@/lib/schemas/validationSchemas";
import TitleField from "@/app/components/form/auctions/TitleField";
import { AuctionDateTimeGroup } from "@/app/components/form/auctions/AuctionDateTimeGroup";
import { combineDateTime } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { AuctionState, AuctionType } from "@prisma/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import { mutate } from "swr";
type FormData = z.infer<typeof auctionCreationSchema>;
type AuctionFormProps = { closeDialog?: () => void; onCreated?: (auction: { id: number; title: string; auctionType: AuctionType }) => void };

const AuctionForm = ({ closeDialog, onCreated }: AuctionFormProps) => {
  const [error, setError] = useState("");
  const [formError, setFormError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();
  const form = useForm<FormData>({
    resolver: zodResolver(auctionCreationSchema),
    defaultValues: {
      title: "",
      startTime: "12:00",
      startMeridian: "AM",
      endTime: "12:00",
      endMeridian: "AM",
      auctionType: AuctionType.ONLINE,
    },
  });

  const validateAuctionDates = (startDateTime: Date, endDateTime: Date) => {
    if (startDateTime <= new Date()) {
      setError("The auction start time must not be in the past.");
    } else if (endDateTime <= new Date()) {
      setError("The auction end time must not be in the past.");
    } else if (endDateTime <= startDateTime) {
      setError("The auction end time must be after the auction start time.");
    } else return true;

    setIsSubmitting(false);
    return false;
  };

  async function onSubmit(formData: FormData) {
    setIsSubmitting(true);
    try {
      const parsedData = auctionCreationSchema.parse(formData);

      // Combine date and time for start and end
      const startDate = combineDateTime(
        parsedData.startDate,
        parsedData.startTime,
        parsedData.startMeridian
      );

      const endDate = combineDateTime(
        parsedData.endDate,
        parsedData.endTime,
        parsedData.endMeridian
      );

      const valid = validateAuctionDates(startDate, endDate);

      if (valid) {
        const auctionData = {
          title: parsedData.title,
          startDate,
          endDate,
          auctionType: parsedData.auctionType,
          auctionState: AuctionState.CREATING,
        };

        const response = await axios.post(`/api/auctions`, auctionData);
        const auction = response.data.auction as { id: number; title: string; auctionType: AuctionType };
        if (onCreated) onCreated(auction)
        mutate("/api/auctions?limit=1000&sort=id_desc")
        router.push(`/admin/auctions/${auction.id}/settings`);
        if (closeDialog) {
          closeDialog();
        }
        router.refresh();
      }
    } catch (error) {
      setError("Something went wrong. Please try again later.");
      setIsSubmitting(false);
    }
  }

  // Should be refactored into a zod refine; how to receive those messages though?
  return (
    <Flex direction="column" gap="3" className="mt-2">
      {error && (
        <Alert variant="destructive">
          <ExclamationTriangleIcon className="h-4 w-4" />
          <AlertTitle>Heads up!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Text className="text-2xl font-semibold pb-6">Create New Auction</Text>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <Flex className="space-x-[88px]" align="end" wrap="wrap">
            <TitleField control={form.control} name="title" />
            <FormField
            control={form.control}
            name="auctionType"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Auction Type</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} defaultValue={field.value ?? AuctionType.ONLINE}>
                    <SelectTrigger className="w-[120px] shrink-0 text-sm">
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AuctionType.ONLINE} className="text-sm">Online</SelectItem>
                      <SelectItem value={AuctionType.HYBRID} className="text-sm">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
            />
          </Flex>

          <AuctionDateTimeGroup
            group="start"
            control={form.control}
            calendarSide="bottom"
          />
          <AuctionDateTimeGroup
            group="end"
            control={form.control}
            calendarSide="bottom"
            labelText={
              (form.watch("auctionType") ?? AuctionType.ONLINE) === AuctionType.HYBRID
                ? "Live Close"
                : "End Date"
            }
            labelTooltip={
              (form.watch("auctionType") ?? AuctionType.ONLINE) === AuctionType.HYBRID
                ? "When a hybrid auction enters live close, lots will remain open until the auctioneer closes them manually, one by one."
                : undefined
            }
          />

          <Flex justify="between" className="pt-5">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (closeDialog) {
                  closeDialog();
                } else {
                  router.back();
                }
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Create New Auction
            </Button>
          </Flex>
        </form>
      </Form>
    </Flex>
  );
};

export default AuctionForm;
